import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const PeriodIndicator = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('period_indicators');
  const [viewItem, setViewItem] = useState(null);
  const [periodIndicators, setPeriodIndicators] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingIndicator, setEditingIndicator] = useState(null);

  useEffect(() => {
    loadPeriodIndicators();
  }, []);

  const loadPeriodIndicators = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/period-indicators`);
      setPeriodIndicators(response.data || []);
    } catch (error) {
      console.error('Error loading period indicators:', error);
      toast.error('Failed to load period indicators');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (indicator = null) => {
    setEditingIndicator(indicator);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data = {
      period_indicator_id: (formData.get('period_indicator_id') || '').toUpperCase(),
      description: formData.get('description')
    };

    try {
      if (editingIndicator) {
        await axios.put(`${BACKEND_URL}/api/period-indicators/${editingIndicator.period_indicator_id}`, {
          description: data.description
        });
        toast.success('Period Indicator updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/period-indicators`, data);
        toast.success('Period Indicator created successfully');
      }
      setIsDialogOpen(false);
      loadPeriodIndicators();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save period indicator');
    }
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Period Indicators</h1>
            <p className="text-sm text-gray-500 mt-1">Manage period indicator configurations (fiscal year, weekly, monthly)</p>
          </div>
        </div>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Period Indicators</h2>
            {writable && (<Button onClick={() => handleOpenDialog()}>
              <Plus className="w-4 h-4 mr-2" />
              Add Period Indicator
            </Button>)}
          </div>

          {isLoading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
              <p className="mt-2 text-gray-500">Loading...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Period Indicator ID</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Description</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {periodIndicators.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="text-center py-8 text-gray-500">
                        No period indicators found. Click &quot;Add Period Indicator&quot; to create one.
                      </td>
                    </tr>
                  ) : (
                    periodIndicators.map((indicator) => (
                      <tr key={indicator.period_indicator_id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 font-mono text-sm font-semibold">{indicator.period_indicator_id}</td>
                        <td className="py-3 px-4 text-sm">{indicator.description}</td>
                        <td className="py-3 px-4 text-right">
                          <RecordActions doctype="period_indicators" onView={() => setViewItem(indicator)} onEdit={() => handleOpenDialog(indicator)} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingIndicator ? 'Edit Period Indicator' : 'Add Period Indicator'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="period_indicator_id">Period Indicator ID * (M, W, or T)</Label>
                  <Input
                    id="period_indicator_id"
                    name="period_indicator_id"
                    defaultValue={editingIndicator?.period_indicator_id || ''}
                    placeholder="M, W, or T"
                    maxLength={1}
                    minLength={1}
                    pattern="[MWTmwt]"
                    title="Must be M (Month), W (Week), or T (Day)"
                    disabled={!!editingIndicator}
                    className="uppercase"
                    required
                    onInput={(e) => {
                      e.target.value = e.target.value.toUpperCase().replace(/[^MWT]/g, '').slice(0, 1);
                    }}
                  />
                  <p className="text-xs text-gray-500 mt-1">M = Month, W = Week, T = Day</p>
                </div>
                <div>
                  <Label>Description *</Label>
                  <Input
                    name="description"
                    defaultValue={editingIndicator?.description || ''}
                    placeholder="e.g., Fiscal Year, Weekly, Monthly"
                    required
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 justify-end pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingIndicator ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default PeriodIndicator;
